// The Organization section list — the org page tab row (OrgSubnav).
//
// NOTE: this file used to claim it was also the source for the sidebar "Organization" group. It is
// not: Sidebar/sidebarNav.ts `BOTTOM_GROUPS` keeps its own (deliberately shorter) list. The two are
// kept in agreement by hand and by `orgSections.test.ts` — see the parity test there.
//
// `adminOnly` mirrors the Jinja `_is_org_admin` gates from Backup/templates/organization/_subnav.html.
// Operator decision 2026-09-10: a normal member sees Dashboard, Billing and Credit ledger; every
// other section is org-admin only. CRM and Domains stay in this list with `feature` keys held at
// `staff` in auth/features.ts — platform admins still see the tabs; launching is a value flip there.
import { paths } from '@/routes/paths';
import { featureRolledOutTo, type FeatureKey } from '@/auth/features';
import type { Tier } from '@/auth/tiers';

export interface OrgSection {
  to: string;
  label: string;
  icon: string; // bootstrap-icons class, e.g. "bi-speedometer2"
  adminOnly: boolean;
  /** Rollout gate — hidden until the feature ships, independently of `adminOnly`. */
  feature?: FeatureKey;
  /** `trailing` items render as icon buttons to the right of the pill row, not as tabs. */
  placement?: 'tab' | 'trailing';
}

export function orgSections(orgSlug: string): OrgSection[] {
  return [
    {
      to: paths.org.dashboard(orgSlug),
      label: 'Dashboard',
      icon: 'bi-speedometer2',
      // Member-visible (operator decision 2026-09-10). NOTE: the backend's dashboard_data endpoint
      // is still `org_admin` — see the phase doc; a member will get a 403 until that changes.
      adminOnly: false,
    },
    {
      to: paths.org.edit(orgSlug),
      label: 'Edit',
      icon: 'bi-pencil-square',
      adminOnly: true,
      placement: 'trailing',
    },
    { to: paths.org.billing(orgSlug), label: 'Billing', icon: 'bi-credit-card', adminOnly: false },
    { to: paths.org.members(orgSlug), label: 'Members', icon: 'bi-people', adminOnly: true },
    {
      to: paths.org.creditLedger(orgSlug),
      label: 'Credit ledger',
      icon: 'bi-coin',
      adminOnly: false,
    },
    { to: paths.org.updates(orgSlug), label: 'Updates', icon: 'bi-journal-text', adminOnly: true },
    {
      to: paths.org.crm(orgSlug),
      label: 'CRM',
      icon: 'bi-briefcase',
      adminOnly: false,
      feature: 'org.crm',
    },
    {
      to: paths.org.domains(orgSlug),
      label: 'Domains',
      icon: 'bi-globe2',
      adminOnly: true,
      feature: 'org.domains',
    },
  ];
}

/** Filter to the sections visible for the current org role and rollout stage. */
export function visibleOrgSections(orgSlug: string, isOrgAdmin: boolean, tier: Tier): OrgSection[] {
  return orgSections(orgSlug).filter(
    (item) =>
      (!item.adminOnly || isOrgAdmin) && (!item.feature || featureRolledOutTo(item.feature, tier)),
  );
}
